import { EMBEDDED_MODE_STORAGE_KEY } from '@/common/domain/constants/embeddedMode';

/**
 * Tenant AppDefault.UrlUM must be this path on the FE origin (data only — not an ERP code change).
 * Example: `http://localhost:3000/user-management/provision?embed=1`
 * Do not point UrlUM at `/`, `/login`, or `/user-management` (list is AuthGuard-protected).
 * ERP appends `dbName`, `requestedBy`, `organizationId`, and `Authorization` (exchange assertion).
 */
export const ERP_UM_LANDING_PATH = '/user-management/provision?embed=1';

/** ERP WebView Users home (TROC-317 chrome-hide). */
export const EUM_EMBED_USERS_PATH = '/user-management?module=user-management&submodule=user_management_users&embed=1';

export function readEmbeddedModeFromStorage(): boolean {
  try {
    return localStorage.getItem(EMBEDDED_MODE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setEmbeddedModeInStorage(enabled: boolean): void {
  try {
    if (enabled) {
      localStorage.setItem(EMBEDDED_MODE_STORAGE_KEY, '1');
      return;
    }
    localStorage.removeItem(EMBEDDED_MODE_STORAGE_KEY);
  } catch {
    // Ignore quota / private-mode failures — chrome stays visible.
  }
}

export function clearEmbeddedMode(): void {
  setEmbeddedModeInStorage(false);
}

/**
 * Parses `embed` query values.
 * @returns `true` / `false` when the param explicitly enables/disables embed; `null` when absent or unknown.
 */
export function parseEmbedQueryValue(raw: string | null): boolean | null {
  if (raw == null) return null;
  const value = raw.trim().toLowerCase();
  if (value === '1' || value === 'true') return true;
  if (value === '0' || value === 'false') return false;
  return null;
}

/**
 * Applies `?embed=` from a search string to localStorage and returns the effective embedded state.
 * When the param is absent, returns the existing storage value (session persistence across navigations).
 */
/** True when ERP opened the provision landing with assertion and/or tenant identity. */
export function hasErpLandingContext(search?: string): boolean {
  const raw = search ?? (typeof window !== 'undefined' ? window.location.search : '');
  const normalized = raw.startsWith('?') ? raw.slice(1) : raw;
  const params = new URLSearchParams(normalized);
  const assertion = (params.get('Authorization') ?? params.get('authorization') ?? '').trim();
  const dbName = (params.get('dbName') ?? '').trim();
  const requestedBy = (params.get('requestedBy') ?? '').trim();
  return Boolean(assertion || (dbName && requestedBy));
}

/**
 * Forces chrome-hide when ERP landing context is present, even if UrlUM omitted `embed=1`.
 */
export function forceEmbedFromErpLanding(search?: string): boolean {
  if (hasErpLandingContext(search)) {
    setEmbeddedModeInStorage(true);
    return true;
  }
  return false;
}

export function syncEmbeddedModeFromSearch(search: string): boolean {
  const normalized = search.startsWith('?') ? search.slice(1) : search;
  const parsed = parseEmbedQueryValue(new URLSearchParams(normalized).get('embed'));
  if (parsed === true) {
    setEmbeddedModeInStorage(true);
    return true;
  }
  if (parsed === false) {
    setEmbeddedModeInStorage(false);
    return false;
  }
  return readEmbeddedModeFromStorage();
}

/** True when URL or sticky ERP-embed storage says chrome should stay hidden. */
export function isEmbeddedSession(search?: string): boolean {
  if (typeof search === 'string') {
    return syncEmbeddedModeFromSearch(search);
  }
  if (typeof window !== 'undefined') {
    return syncEmbeddedModeFromSearch(window.location.search);
  }
  return readEmbeddedModeFromStorage();
}

/**
 * Ensures `embed=1` is on a path when the ERP embed session is active
 * (so login / hard redirects do not drop chrome-hide).
 */
export function withEmbedQueryIfNeeded(path: string, embedded = readEmbeddedModeFromStorage()): string {
  if (!embedded) return path;
  const hashIndex = path.indexOf('#');
  const hash = hashIndex >= 0 ? path.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path;
  const qIndex = withoutHash.indexOf('?');
  const pathname = qIndex >= 0 ? withoutHash.slice(0, qIndex) : withoutHash;
  const search = qIndex >= 0 ? withoutHash.slice(qIndex + 1) : '';
  const params = new URLSearchParams(search);
  if (parseEmbedQueryValue(params.get('embed')) === true) {
    return `${pathname}?${params.toString()}${hash}`;
  }
  params.set('embed', '1');
  const next = params.toString();
  return `${pathname}?${next}${hash}`;
}
